import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Save, X, Plus } from 'lucide-react';
import { doc, getDoc, updateDoc, collection, onSnapshot, query, orderBy } from 'firebase/firestore';
import { db } from '../../config/firebase';
import ImageUpload from '../../components/ui/ImageUpload';

const EditTeamPage = () => {
  const { teamId } = useParams();
  const navigate = useNavigate();
  const [team, setTeam] = useState(null);
  const [teamName, setTeamName] = useState('');
  const [teamLogo, setTeamLogo] = useState('');
  const [selectedPlayers, setSelectedPlayers] = useState([]);
  const [availablePlayers, setAvailablePlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchTeam = async () => {
      try {
        const teamDoc = await getDoc(doc(db, 'teams', teamId));
        if (teamDoc.exists()) {
          const teamData = { id: teamDoc.id, ...teamDoc.data() };
          setTeam(teamData);
          setTeamName(teamData.name);
          setTeamLogo(teamData.logo || '');
          setSelectedPlayers(teamData.players || []);
        } else {
          setError('Team not found');
        }
      } catch (err) {
        setError('Failed to load team details');
        console.error('Error fetching team:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchTeam();
  }, [teamId]);

  useEffect(() => {
    const playersQuery = query(
      collection(db, 'players'),
      orderBy('name', 'asc')
    );

    const unsubscribe = onSnapshot(playersQuery, (snapshot) => {
      const players = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setAvailablePlayers(players);
    });

    return unsubscribe;
  }, []);

  const handleAddPlayer = (player) => {
    if (selectedPlayers.find(p => p.id === player.id)) {
      setError('Player already selected');
      return;
    }
    setSelectedPlayers([...selectedPlayers, { ...player, selected: true }]);
    setError('');
  };

  const handleRemovePlayer = (playerId) => {
    setSelectedPlayers(selectedPlayers.filter(p => p.id !== playerId));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    if (!teamName.trim()) {
      setError('Team name cannot be empty.');
      setSaving(false);
      return;
    }

    try {
      await updateDoc(doc(db, 'teams', teamId), {
        name: teamName,
        logo: teamLogo,
        players: selectedPlayers,
        playerIds: selectedPlayers.map(p => p.id),
        updatedAt: new Date().toISOString()
      });

      navigate('/teams');
    } catch (error) {
      setError('Failed to update team. Please try again.');
      console.error('Error updating team:', error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="loading">Loading team details...</div>
      </div>
    );
  }

  if (error && !team) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="text-center py-12">
          <h2 className="text-2xl font-bold mb-4" style={{ color: '#1C563D' }}>
            {error}
          </h2>
          <button
            onClick={() => navigate('/teams')}
            className="btn btn-primary"
          >
            Back to Teams
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <button
          onClick={() => navigate('/teams')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-800 mb-4"
        >
          <ArrowLeft size={20} />
          Back to Teams
        </button>
        <h1 className="text-3xl font-bold mb-2" style={{ color: '#1C563D' }}>
          Edit Team
        </h1>
        <p className="text-gray-600">
          Update team details and player roster
        </p>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Team Form */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1 }}
          className="card"
        >
          <h2 className="text-xl font-semibold mb-4" style={{ color: '#1C563D' }}>
            Team Details
          </h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="form-group">
              <label htmlFor="teamName" className="form-label">
                Team Name *
              </label>
              <input
                id="teamName"
                type="text"
                required
                className="form-input"
                value={teamName}
                onChange={(e) => setTeamName(e.target.value)}
                placeholder="Enter team name"
              />
            </div>

            <div className="form-group">
              <ImageUpload
                currentImageUrl={teamLogo}
                onImageChange={setTeamLogo}
                folder="team-logos"
                aspectRatio="square"
                className="mb-4"
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                Selected Players ({selectedPlayers.length})
              </label>
              {selectedPlayers.length > 0 ? (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {selectedPlayers.map((player) => (
                    <div
                      key={player.id}
                      className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg"
                    >
                      <span className="font-medium">{player.name}</span>
                      <button
                        type="button"
                        onClick={() => handleRemovePlayer(player.id)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-sm">
                  No players selected yet
                </p>
              )}
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={saving}
              className="btn btn-primary w-full"
              style={{
                backgroundColor: saving ? '#999' : '#1C563D',
                cursor: saving ? 'not-allowed' : 'pointer'
              }}
            >
              {saving ? 'Saving Changes...' : 'Save Changes'}
            </button>
          </form>
        </motion.div>

        {/* Available Players */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
          className="card"
        >
          <h2 className="text-xl font-semibold mb-4" style={{ color: '#1C563D' }}>
            Available Players
          </h2>

          {availablePlayers.length > 0 ? (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {availablePlayers.map((player) => (
                <div
                  key={player.id}
                  className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50"
                >
                  <div>
                    <div className="font-medium">{player.name}</div>
                    <div className="text-sm text-gray-600">
                      {player.teamName || 'No team assigned'}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleAddPlayer(player)}
                    disabled={selectedPlayers.find(p => p.id === player.id)}
                    className="btn btn-sm btn-primary"
                    style={{
                      backgroundColor: selectedPlayers.find(p => p.id === player.id) ? '#999' : '#1C563D',
                      cursor: selectedPlayers.find(p => p.id === player.id) ? 'not-allowed' : 'pointer'
                    }}
                  >
                    <Plus size={16} />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <p>No available players found</p>
              <button
                onClick={() => navigate('/players/create')}
                className="btn btn-primary mt-4"
              >
                Create Players
              </button>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default EditTeamPage;
